import {firstValueFrom, Observable, Subject} from 'rxjs';

import {ICredentialPostRequest} from '../interfaces/credential-post-request.interface';
import {ICredentialPutRequest} from '../interfaces/credential-put-request.interface';
import {ISafeCredential} from '../interfaces/safe-credential.interface';
import {Injectable} from '@angular/core';
import {environment} from '../../../environments/environment';
import {HttpServiceBase} from './http-service-base';

@Injectable()
export class CredentialsManager extends HttpServiceBase{

    public static readonly PROVIDER_ITSCOPE = 'itscope';
    public static readonly PROVIDER_CADOOZ = 'cadooz';

    private credentialsObservable: Observable<void>;
    private credentialsSubject: Subject<void>;

    private credentials: ISafeCredential[] = [];
    private itScopeServerAddress: string = environment.itScopeServerAddress;

    constructor( ) {
        super();
        this.credentialsSubject = new Subject<void>();
        this.credentialsObservable = this.credentialsSubject.asObservable();

        this.removeCredentials();
    }

    saveCredentials(credentials: ISafeCredential[]): void {
        if (credentials) {
            this.credentials = credentials;
            this.credentialsSubject.next();
            // console.log(this.credentials);
        }
    }

    removeCredentials(): void {
        this.credentials = [];
    }

    hasCredentials(): boolean {
        if (this.hasCredential(CredentialsManager.PROVIDER_ITSCOPE, false)) {
            return true;
        }
        if (this.hasCredential(CredentialsManager.PROVIDER_CADOOZ, false)) {
            return true;
        }
        return false;
    }

    hasCredential(provider: string, withLogin: boolean = false): boolean {
        const credential = this.getCredential(provider);

        if (!credential) {
            return false;
        }
        if (withLogin && !credential.securitySet) {
            return false;
        }
        return true;
    }

    hasITScopeCredentialSecuritySet() {
        const credential = this.getCredential(CredentialsManager.PROVIDER_ITSCOPE);
        if (!credential) {
            return false;
        }
        return credential.securitySet;
    }

    getCredentials(): ISafeCredential[] {
        return this.credentials;
    }

    getCredential(provider: string): ISafeCredential {
        for (const credential of this.credentials) {
            if (credential.provider === provider) {
                return credential;
            }
        }
        return null;
    }

    getCredentialsObservable(): Observable<void> {
        return this.credentialsObservable;
    }

    async createCredentials(userId: number, body: ICredentialPostRequest): Promise<ISafeCredential[]> {


        try {
            const response: any = await firstValueFrom(
                this.httpClient.post<any>(`${this.serverAddress}/v1/users/${userId}/credentials`, body, {
                    observe: 'body'
                })
            );

            for (const credential of body.credentials) {
                // do not save security/password
                const safeCredential: ISafeCredential = {
                    provider: credential.provider,
                    login: credential.login,
                } as ISafeCredential;
                this.credentials.push(safeCredential);
            }
            this.credentialsSubject.next();
            // console.log('createCredentials', this.credentials);
            // return response;
            return this.credentials;
        } catch (error) {
            throw error;
        }
    }

    async updateCredential(userId: number, provider: string, body: ICredentialPutRequest): Promise<ISafeCredential[]> {
        try {
            const response: any = await firstValueFrom(
                this.httpClient.put<any>(`${this.serverAddress}/v1/users/${userId}/credentials/${provider}`, body, {
                    observe: 'body'
                })
            );
            const safeCredential: ISafeCredential = this.getCredential(provider);
            if (safeCredential) {
                // do not save security/password
                safeCredential.login = body.login;
            }
            this.credentialsSubject.next();
            // console.log(this.credentials);
            // return response;
            return this.credentials;
        } catch (error) {
            throw error;
        }
    }

    async deleteCredential(userId: number, provider: string): Promise<ISafeCredential[]> {
        try {
            const response: any = await firstValueFrom(this.httpClient
                .delete<any>(`${this.serverAddress}/v1/users/${userId}/credentials/${provider}`, {
                    observe: 'body'
                })
            );
            const safeCredential: ISafeCredential = this.getCredential(provider);
            if (safeCredential) {
                // do not save security/password
                safeCredential.login = null;
            }
            this.credentialsSubject.next();
            // console.log(this.credentials);
            // return response;
            return this.credentials;
        } catch (error) {
            throw error;
        }
    }

    async getITScopeAPIKey(userName: string, password: string): Promise<string> {
        return await firstValueFrom(
            this.httpClient.get(`${this.itScopeServerAddress}/user/apikey`, {
                observe: 'body',
                responseType: 'text',
                headers: {
                    'Authorization': 'Basic ' + btoa(`${userName}:${password}`)
                },
            })
        );
    }

    async createItScopeCredential(userId: number, apiKey: string): Promise<any> {
        const body = {
            credentials: [
                {
                    'provider': CredentialsManager.PROVIDER_ITSCOPE,
                    'login': null,
                    'security': apiKey
                }
            ]
        };
        return await firstValueFrom(this.httpClient
            .post<any>(`${this.serverAddress}/v1/users/${userId}/credentials`, body, {
                observe: 'body'
            })
        );
    }

    async updateItScopeCredentials(userId: number, apiKey: string): Promise<void> {

        const body = {
            'login': null,
            'security': apiKey
        };
        try {
            const response: any = await firstValueFrom(this.httpClient
                .put<any>(`${this.serverAddress}/v1/users/${userId}/credentials/${CredentialsManager.PROVIDER_ITSCOPE}`, body, {
                    observe: 'body'
                })
            );
            return;
        } catch (error) {
            throw error;
        }
    }

}
